import React from "react";
import { Eye, Pencil, Trash2, Users, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "src/components/ui/table";

function relativeTime(isoString) {
  if (!isoString) return "—";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function SkeletonRow() {
  return (
    <TableRow>
      {[40, 160, 120, 140, 100, 100, 80].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function EmptyState({ onNewContact }) {
  return (
    <TableRow>
      <TableCell colSpan={7}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">
            No contacts found
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Get started by adding your first contact
          </p>
          <Button size="sm" className="mt-4" onClick={onNewContact}>
            + Add New
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ContactsTable({
  contacts = [],
  loading,
  meta,
  page,
  onPageChange,
  onEdit,
  onDelete,
  onNewContact,
}) {
  const [sortField, setSortField] = React.useState(null)
  const [sortDirection, setSortDirection] = React.useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedContacts = React.useMemo(() => {
    if (!sortField || !sortDirection) return contacts
    return [...contacts].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.name || ""
        bVal = b.name || ""
      } else if (sortField === "phone") {
        aVal = a.phone || ""
        bVal = b.phone || ""
      } else if (sortField === "email") {
        aVal = a.email || ""
        bVal = b.email || ""
      } else if (sortField === "type") {
        aVal = a.contact_type || ""
        bVal = b.contact_type || ""
      } else if (sortField === "added") {
        aVal = a.created_at || ""
        bVal = b.created_at || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [contacts, sortField, sortDirection])

  const renderSortableHeader = (label, field) => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className="inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black"
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  const startRow = (page - 1) * (meta?.per_page || 20) + 1;
  const endRow = Math.min(startRow + (contacts.length - 1), meta?.total || 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="min-w-[160px] text-black">{renderSortableHeader("Name", "name")}</TableHead>
            <TableHead className="min-w-[140px] text-black">{renderSortableHeader("Phone", "phone")}</TableHead>
            <TableHead className="min-w-[160px] text-black">{renderSortableHeader("Email", "email")}</TableHead>
            <TableHead className="w-[100px] text-black">{renderSortableHeader("Type", "type")}</TableHead>
            <TableHead className="w-[100px] text-black">{renderSortableHeader("Added", "added")}</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : contacts.length === 0 ? (
            <EmptyState onNewContact={onNewContact} />
          ) : (
            sortedContacts.map((item, idx) => (
              <TableRow key={item.id}>
                <TableCell className="text-xs text-slate-400">
                  {startRow + idx}
                </TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900 text-[13px]">
                    {item.name}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-700">
                    {item.phone || "—"}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-700">
                    {item.email || "—"}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-600 capitalize">
                    {item.contact_type || "—"}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[12px] text-slate-500">
                    {relativeTime(item.created_at)}
                  </p>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(item)}
                      title="Edit contact"
                    >
                      <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(item)}
                      title="Delete contact"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!loading && contacts.length > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500">
            Showing {startRow}–{endRow} of {meta?.total ?? 0} contacts
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              ← Prev
            </Button>
            <span className="px-3 text-xs text-slate-500">
              {page} / {meta?.total_pages ?? 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= (meta?.total_pages ?? 1)}
              onClick={() => onPageChange(page + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
